import { useState, useEffect } from "react";
import axios from "axios";

export default function useAuth() {
  const [user, setUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem("maaze_user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  /* ================= LOGIN API ================= */
  const login = async ({ email, password }) => {
    setAuthError("");
    setAuthLoading(true);

    try {
      const response = await axios.post(
        "https://maize.norissolutions.in/Php/public/api/auth/login",
        { email, password }
      );

      const data = response.data;

      // ✅ Correct token extraction
      const token = data.data.token;
      const userData = data.data.user;

      if (!token) {
        setAuthError("Token not received from server.");
        return false;
      }

      // Store token
      sessionStorage.setItem("maaze_token", token);

      // Set axios default header
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${token}`;

      const loggedUser = {
        id: userData.user_id,
        user_id: userData.user_id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        season_id: data.data.active_season?.season_id,
        season_name: data.data.active_season?.season_name,
        organiser_id: data.data.organiser_id // 🔥 ADD THIS
      };

      // Always reset active page on login so the role's default dashboard shows
      sessionStorage.removeItem('maaze_active_page');

      setUser(loggedUser);
      sessionStorage.setItem("maaze_user", JSON.stringify(loggedUser));

      return true;

    } catch (err) {
      console.log("Login Error:", err.response?.data);

      setAuthError(
        err.response?.data?.message || "Invalid email or password."
      );

      return false;

    } finally {
      setAuthLoading(false);
    }
  };

  /* ================= REGISTER API ================= */
  const register = async ({ name, email, password, role, mobile }) => {
    setAuthError("");
    setAuthLoading(true);

    try {
      await axios.post(
        "https://maize.norissolutions.in/Php/public/api/auth/register",
        { name, email, password, role, mobile },
      );

      return true;
    } catch (err) {
      if (err.response?.data?.message) {
        setAuthError(err.response.data.message);
      } else {
        setAuthError("Registration failed.");
      }

      return false;
    } finally {
      setAuthLoading(false);
    }
  };
  /* ================= ORGANISER REGISTER API ================= */
  const organiserRegister = async ({ name, email, password, mobile, season_id }) => {
    setAuthError("");
    setAuthLoading(true);

    try {
      const token = sessionStorage.getItem("maaze_token");

      const response = await axios.post(
        "https://maize.norissolutions.in/Php/public/api/organiser/users",
        {
          name,
          email,
          password,
          mobile,
          season_id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`   // 🔥 VERY IMPORTANT
          }
        }
      );

      return {
        success: true,
        data: response.data,
        message: response.data?.message || "Organiser created successfully"
      };

    } catch (err) {
      console.log("Organiser Register Error:", err.response?.data);

      const msg =
        err.response?.data?.message || "Organiser creation failed";

      setAuthError(msg);

      return {
        success: false,
        message: msg
      };

    } finally {
      setAuthLoading(false);
    }
  };
  /* ================= LOGOUT ================= */
  const logout = () => {
    setUser(null);
    sessionStorage.removeItem("maaze_user");
    sessionStorage.removeItem("maaze_token");
    sessionStorage.removeItem("maaze_season_id");
    sessionStorage.removeItem("maaze_active_page");
    sessionStorage.removeItem("maaze_organiser");
    delete axios.defaults.headers.common["Authorization"];
    setAuthError("");
  };

  /* ================= changePassword ================= */

  const changePassword = async (currentPassword, newPassword) => {
    const token = sessionStorage.getItem("maaze_token");
    try {
      const token = sessionStorage.getItem("maaze_token");

      const response = await axios.post(
        "https://maize.norissolutions.in/Php/public/api/auth/change-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return {
        success: true,
        message: response.data.message || "Password changed successfully",
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Password change failed",
      };
    }
  };

  /* ================= REFRESH TOKEN API ================= */
  const refreshToken = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");
      const storedUser = JSON.parse(sessionStorage.getItem("maaze_user"));

      if (!token || !storedUser) {
        console.log("No session found");
        return { success: false };
      }

      const response = await axios.post(
        "https://maize.norissolutions.in/Php/public/api/auth/refresh",
        {
          user_id: storedUser.id,
          email: storedUser.email,
          role: storedUser.role,
          name: storedUser.name,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newToken = response.data?.data?.token;

      if (!newToken) {
        console.log("No new token returned");
        return { success: false };
      }

      // 🔥 Replace old token
      sessionStorage.setItem("maaze_token", newToken);

      // Update axios header
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${newToken}`;

      console.log("Token refreshed successfully");

      return { success: true };

    } catch (err) {
      console.log("Refresh error:", err.response?.data);
      return { success: false };
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("maaze_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${token}`;
    }
  }, []);


  return {
    user,
    authError,
    authLoading,
    login,
    register,
    logout,
    refreshToken,
    changePassword,
    setAuthError,
    organiserRegister
  };
}
